import { SupportedChainId } from 'config/chains'
import { PORTFOLIO_REFETCH_POLICY } from 'config/RefetchPolicies'
import { SubgraphType } from 'config/subgraph'
import { useConnection } from 'hooks/useConnection'
import { useSubgraphConfig } from 'hooks/useSubgraphConfig'
import { useGetTokenPrices } from 'hooks/useTokenPrice'
import { GetPortfolioV3PositionsQuery, useGetPortfolioV3PositionsQuery } from 'queries/v3/generated'
import { useMemo } from 'react'
import { ProcessedV3Position } from 'types/subgraph'
import { buildV3Position } from 'utils/portfolio/buildV3Position'
import { filterV3Positions, normalizeDisplaySymbol } from 'utils/portfolio/filterHelpers'
import { Address } from 'viem'

import { useTokensFromList } from './useTokenList'

type V3PositionFromQuery = GetPortfolioV3PositionsQuery['positions'][0]

function processV3Position(
  position: V3PositionFromQuery,
  chainId: SupportedChainId,
  tokenData?: Record<Address, { logoUri?: string; decimals: number; symbol: string; name: string }>,
  tokenPrices?: Record<Address, number>
): ProcessedV3Position {
  const sdkPosition = buildV3Position(position, chainId)

  // Keep subgraph data and merge additional token info using spread operator
  const token0Address = position.token0.id.toLowerCase() as Address
  const token1Address = position.token1.id.toLowerCase() as Address

  const enrichedToken0 = {
    ...position.token0, // Keep all subgraph data
    symbol: normalizeDisplaySymbol(position.token0.symbol),
    ...(tokenData?.[token0Address] && { logoUri: tokenData[token0Address].logoUri }), // Merge logoUri
    ...(tokenPrices?.[token0Address] && { currentPrice: tokenPrices[token0Address] }), // Merge price
  }

  const enrichedToken1 = {
    ...position.token1, // Keep all subgraph data
    symbol: normalizeDisplaySymbol(position.token1.symbol),
    ...(tokenData?.[token1Address] && { logoUri: tokenData[token1Address].logoUri }), // Merge logoUri
    ...(tokenPrices?.[token1Address] && { currentPrice: tokenPrices[token1Address] }), // Merge price
  }

  const enrichedPool = {
    ...position.pool, // Keep all subgraph pool data
    token0: {
      ...position.pool.token0, // Keep all subgraph pool token0 data
      symbol: normalizeDisplaySymbol(position.pool.token0.symbol),
      ...(tokenData?.[token0Address] && { logoUri: tokenData[token0Address].logoUri }),
      ...(tokenPrices?.[token0Address] && { currentPrice: tokenPrices[token0Address] }),
    },
    token1: {
      ...position.pool.token1, // Keep all subgraph pool token1 data
      symbol: normalizeDisplaySymbol(position.pool.token1.symbol),
      ...(tokenData?.[token1Address] && { logoUri: tokenData[token1Address].logoUri }),
      ...(tokenPrices?.[token1Address] && { currentPrice: tokenPrices[token1Address] }),
    },
  }

  return {
    nftId: position.id,
    owner: position.owner as Address,
    poolAddress: position.pool.id as Address,
    sdkPosition,
    enrichedToken0,
    enrichedToken1,
    enrichedPool,
    depositedToken0: position.depositedToken0,
    depositedToken1: position.depositedToken1,
    withdrawnToken0: position.withdrawnToken0,
    withdrawnToken1: position.withdrawnToken1,
    collectedFeesToken0: position.collectedFeesToken0,
    collectedFeesToken1: position.collectedFeesToken1,
    uncollectedFeesToken0: '0',
    uncollectedFeesToken1: '0',
    poolTotalValueLockedUSD: position.pool.totalValueLockedUSD,
    createdAt: position.transaction.timestamp,
    createdAtBlock: position.transaction.blockNumber,
  }
}

export function useV3Positions(options?: {
  enabled?: boolean
  refetchInterval?: number
  filter?: string
  enableLpTokens?: boolean
}) {
  const { account: userAddress, isConnected, chainId } = useConnection()
  const subgraphConfig = useSubgraphConfig(SubgraphType.V3)
  const { data: tokens } = useTokensFromList({
    enabled: options?.enabled !== false,
    enableLpTokens: options?.enableLpTokens,
  })

  // Create token data map
  const tokenData = useMemo(() => {
    if (!tokens) return {}
    const map: Record<Address, { logoUri?: string; decimals: number; symbol: string; name: string }> = {}
    tokens.forEach((token) => {
      const address = token.address.toLowerCase() as Address
      map[address] = {
        logoUri: token.logoUri,
        decimals: token.decimals,
        symbol: token.symbol,
        name: token.name,
      }
    })
    return map
  }, [tokens])

  const v3PositionsQuery = useGetPortfolioV3PositionsQuery(
    subgraphConfig,
    {
      owner: userAddress?.toLowerCase() as any,
      first: 1000,
      skip: 0,
    },
    {
      enabled: isConnected && !!userAddress && options?.enabled !== false,
      ...PORTFOLIO_REFETCH_POLICY,
      refetchInterval: options?.refetchInterval ?? PORTFOLIO_REFETCH_POLICY.refetchInterval,
    }
  )

  // Extract token addresses for price fetching
  const tokenAddresses = useMemo(() => {
    if (!v3PositionsQuery.data?.positions) return []
    const addresses = new Set<Address>()
    v3PositionsQuery.data.positions.forEach((position) => {
      addresses.add(position.token0.id.toLowerCase() as Address)
      addresses.add(position.token1.id.toLowerCase() as Address)
    })
    return Array.from(addresses)
  }, [v3PositionsQuery.data?.positions])

  const { prices: tokenPricesArray, isLoading: isPricesLoading } = useGetTokenPrices(tokenAddresses, {
    enabled: tokenAddresses.length > 0 && options?.enabled !== false,
  })

  const tokenPrices = useMemo(() => {
    if (!tokenPricesArray) return undefined
    return tokenAddresses.reduce((acc: Record<Address, number>, address, index) => {
      acc[address] = tokenPricesArray[index] ?? 0
      return acc
    }, {})
  }, [tokenAddresses, tokenPricesArray])

  // Process positions with enriched token data
  const processedPositions = useMemo(() => {
    if (!v3PositionsQuery.data?.positions) return []
    return v3PositionsQuery.data.positions.map((position) =>
      processV3Position(position, chainId, tokenData, tokenPrices)
    )
  }, [v3PositionsQuery.data?.positions, chainId, tokenData, tokenPrices])

  // Filter positions
  const filteredPositions = useMemo(() => {
    return filterV3Positions(processedPositions, options?.filter || '')
  }, [processedPositions, options?.filter])

  return {
    ...v3PositionsQuery,
    data: filteredPositions,
    allPositions: processedPositions,
    tokenPrices,
    isLoading: v3PositionsQuery.isLoading || isPricesLoading,
  }
}
